'use client'

import { type ChangeEvent, type FormEvent, useState } from 'react'
import { Button } from '@/components/ui/button'

const initialForm = {
  name: '',
  email: '',
  phone: '',
  message: '',
}

type FormState = typeof initialForm

type FormErrors = Partial<Record<keyof FormState, string>>

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ContactForm() {
  const [form, setForm] = useState<FormState>(initialForm)
  const [errors, setErrors] = useState<FormErrors>({})
  const [successMessage, setSuccessMessage] = useState('')

  const validate = (values: FormState) => {
    const nextErrors: FormErrors = {}

    if (!values.name.trim()) {
      nextErrors.name = 'Vui lòng nhập họ và tên.'
    }

    if (!values.email.trim()) {
      nextErrors.email = 'Vui lòng nhập email.'
    } else if (!emailPattern.test(values.email.trim())) {
      nextErrors.email = 'Email không hợp lệ.'
    }

    if (!values.phone.trim()) {
      nextErrors.phone = 'Vui lòng nhập số điện thoại.'
    }

    if (!values.message.trim()) {
      nextErrors.message = 'Vui lòng nhập nội dung yêu cầu.'
    }

    return nextErrors
  }

  const handleChange = (field: keyof FormState) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }))
    setSuccessMessage('')

    if (errors[field]) {
      setErrors((current) => ({
        ...current,
        [field]: undefined,
      }))
    }
  }

const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const nextErrors = validate(form)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      setSuccessMessage('')
      return
    }

    setErrors({})
    setForm(initialForm)
    setSuccessMessage(
      'Gửi yêu cầu thành công. Cảm ơn bạn đã gửi yêu cầu. Chúng tôi sẽ liên hệ lại trong thời gian sớm nhất.',
    )
  }

  return (
    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
      <h3 className="text-2xl font-bold text-white mb-6">Gửi Yêu Cầu</h3>
      {successMessage ? (
        <div className="rounded-xl border border-emerald-300 bg-emerald-500/10 p-4 mb-6 text-sm text-emerald-700">
          {successMessage}
        </div>
      ) : null}
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Họ và tên"
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'name-error' : undefined}
            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {errors.name ? (
            <p id="name-error" className="mt-2 text-sm text-destructive">
              {errors.name}
            </p>
          ) : null}
        </div>

        <div>
          <input
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            placeholder="Email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {errors.email ? (
            <p id="email-error" className="mt-2 text-sm text-destructive">
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <input
            type="tel"
            value={form.phone}
            onChange={handleChange('phone')}
            placeholder="Số điện thoại"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {errors.phone ? (
            <p id="phone-error" className="mt-2 text-sm text-destructive">
              {errors.phone}
            </p>
          ) : null}
        </div>

        <div>
          <textarea
            value={form.message}
            onChange={handleChange('message')}
            placeholder="Nội dung yêu cầu"
            rows={4}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? 'message-error' : undefined}
            className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          {errors.message ? (
            <p id="message-error" className="mt-2 text-sm text-destructive">
              {errors.message}
            </p>
          ) : null}
        </div>

        <Button type="submit" className="w-full bg-primary text-white hover:bg-primary/90 font-semibold">
          Gửi Yêu Cầu
        </Button>
      </form>
    </div>
  )
}
